/**
 * Turn timer (UX-SPEC §5 "Timer"): a circular countdown ring + mm:ss on the
 * active player's card, amber under 20s, red with a gentle pulse under 10s,
 * a tick each second under 5s on my own turn. Uses the server clock offset.
 * On my own turn, crossing 20s and 10s is also announced to screen readers
 * (the ring itself stays aria-live="off": a per-second countdown is noise).
 * Untimed games show the elapsed turn time, subtly.
 */
import { useEffect, useRef } from 'react';
import clsx from 'clsx';
import { sfx } from '../audio/sfx';
import { useServerNow } from '../hooks/useNow';
import { formatClock, formatElapsed, secondsLeft } from '../lib/time';
import { useUiStore } from '../state/uiStore';
import './Timer.css';

/** Seconds left at which my turn's low-time warning is spoken (amber, red). */
const ANNOUNCE_AT = [20, 10] as const;

interface TimerProps {
  /** Epoch ms (server clock) when the turn times out; null = untimed. */
  deadline: number | null;
  turnStartedAt: number;
  limitSec: number | null;
  /** Tick sounds only on my own turn. */
  mine: boolean;
  size?: 'md' | 'sm';
}

export type TimerLevel = 'normal' | 'warn' | 'danger';

export function timerLevel(secs: number): TimerLevel {
  if (secs < 10) return 'danger';
  if (secs < 20) return 'warn';
  return 'normal';
}

export function Timer({ deadline, turnStartedAt, limitSec, mine, size = 'md' }: TimerProps) {
  const now = useServerNow(true, 1000);
  const lastTick = useRef<number | null>(null);
  /** This turn's low-time warnings: the last second seen and the thresholds already spoken. */
  const warnings = useRef<{ turn: number; secs: number; spoken: readonly number[] } | null>(null);
  const timed = deadline !== null && limitSec !== null;
  const remaining = timed ? deadline - now : 0;
  const secs = secondsLeft(remaining);

  useEffect(() => {
    if (!timed || !mine) return;
    if (secs > 0 && secs <= 5 && lastTick.current !== secs) {
      lastTick.current = secs;
      sfx.tick();
    }
    // Speak once per threshold per turn, on the second the countdown crosses
    // it (a skipped second still counts). Mounting mid-turn below a threshold
    // (reconnect, layout switch) is not a crossing, so it stays quiet.
    const w = warnings.current?.turn === turnStartedAt ? warnings.current : null;
    const crossed = w && secs > 0 ? ANNOUNCE_AT.filter((t) => w.secs > t && secs <= t && !w.spoken.includes(t)) : [];
    warnings.current = { turn: turnStartedAt, secs, spoken: [...(w?.spoken ?? []), ...crossed] };
    if (crossed.length > 0) useUiStore.getState().announce(`${secs} seconds left`);
  }, [secs, timed, mine, turnStartedAt]);

  if (!timed) {
    return (
      <span className={clsx('timer timer--elapsed', `timer--${size}`)} title="Time on this turn">
        <span className="sr-only">Turn time </span>
        <span className="tabular">{formatElapsed(now - turnStartedAt)}</span>
      </span>
    );
  }

  const fraction = Math.max(0, Math.min(1, remaining / (limitSec * 1000)));
  const level = timerLevel(secs);
  const r = 18;
  const c = 2 * Math.PI * r;
  return (
    <span
      className={clsx('timer', `timer--${size}`, `timer--${level}`)}
      role="timer"
      aria-label={`${secs} seconds left`}
      aria-live="off"
    >
      <svg viewBox="0 0 44 44" className="timer__ring" aria-hidden="true">
        <circle cx="22" cy="22" r={r} className="timer__track" />
        <circle
          cx="22"
          cy="22"
          r={r}
          className="timer__arc"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - fraction)}
          transform="rotate(-90 22 22)"
        />
      </svg>
      <span className="timer__text tabular" aria-hidden="true">
        {formatClock(remaining)}
      </span>
    </span>
  );
}
